import * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

interface AuthContextReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  needsTenant: boolean;
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  authError: string | null;
}

/**
 * A wrapper for routes that should be protected by authentication
 * Redirects to login if user is not authenticated
 * Shows error message if user is not an admin
 * Redirects to create-tenant if admin user has no tenants
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    needsTenant,
    isAdmin,
    isPlatformAdmin,
    authError
  }: AuthContextReturn = useAuthContext();
  
  const location = useLocation();
  const authExcludedPaths: string[] = ['/login'];
  
  // Show loading indicator while checking authentication
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <p>Loading...</p>
      </div>
    );
  }
  
  // Redirect to login if not authenticated and not on an excluded path
  if (!isAuthenticated && !authExcludedPaths.includes(location.pathname)) {
    return <Navigate to="/login" replace />;
  }
  
  // Show error message if user is authenticated but not an admin
  if (isAuthenticated && !isAdmin && !isPlatformAdmin) {
    const handleBackToLogin = (): void => {
      window.location.href = '/login';
    };

    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2 style={{ 
          color: '#721c24', 
          marginBottom: '20px' 
        }}>
          Access Restricted
        </h2>
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24',
          borderRadius: '4px',
          maxWidth: '500px',
          border: '1px solid #f5c6cb'
        }}>
          <p>{authError || 'Only administrators can access this application.'}</p>
        </div>
        <button
          onClick={handleBackToLogin}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Back to Login
        </button>
      </div>
    );
  }
  
  // Redirect to create-tenant if admin user has no tenants, except if already on create-tenant page
  if (isAuthenticated && isAdmin && needsTenant && !isPlatformAdmin && location.pathname !== '/create-tenant') {
    return <Navigate to="/create-tenant" replace />;
  }

  // Render the protected content if conditions are met
  return <>{children}</>;
};

export default ProtectedRoute; 